import React, { ReactNode } from 'react';
import { Result, Button } from 'antd';
import { usePermissions } from '../../contexts/PermissionContext';
import { useTranslation } from 'react-i18next';

interface PermissionGuardProps {
  permission?: string;
  permissions?: string[];
  /** Require this menu model (model_role / model_user) in addition to permission(s) */
  menuModelKey?: string;
  requireAll?: boolean;
  role?: string | string[];
  resource?: string;
  action?: string;
  redirectTo?: string;
  fallback?: ReactNode;
  children: ReactNode;
}

/**
 * PermissionGuard component - Protects routes based on permissions
 *
 * @example
 * <Route path="/orders" exact>
 *   <PermissionGuard permission="view_orders">
 *     <Orders />
 *   </PermissionGuard>
 * </Route>
 */
const PermissionGuard: React.FC<PermissionGuardProps> = ({
  permission,
  permissions,
  menuModelKey,
  requireAll = false,
  role,
  resource,
  action,
  redirectTo = '/dashboard',
  fallback,
  children,
}) => {
  const {
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasRole,
    canAccess,
    canAccessMenuRoute,
    isLoading,
  } = usePermissions();
  const { t } = useTranslation();

  // Show loading state while permissions are being loaded
  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <div>Loading...</div>
      </div>
    );
  }

  // Only Super Admin bypasses permission checks
  // Administrators must have explicit permissions
  if (hasRole('superadministrator')) {
    return <>{children}</>;
  }

  let hasAccess = false;

  // Check by role
  if (role) {
    hasAccess = hasRole(role);
  }
  // Menu model + direct permission(s) — reports and other model-scoped routes
  else if (menuModelKey) {
    const permList =
      permissions && permissions.length > 0 ? permissions : permission ? [permission] : [];
    hasAccess = canAccessMenuRoute(menuModelKey, permList, requireAll);
  }
  // Check by single permission
  else if (permission) {
    hasAccess = hasPermission(permission);
  }
  // Check by multiple permissions
  else if (permissions && permissions.length > 0) {
    hasAccess = requireAll ? hasAllPermissions(permissions) : hasAnyPermission(permissions);
  }
  // Check by resource and action
  else if (resource && action) {
    hasAccess = canAccess(resource, action);
  }
  // If no condition provided, deny access
  else {
    hasAccess = false;
  }

  if (hasAccess) {
    return <>{children}</>;
  }

  // Show custom fallback if provided
  if (fallback) {
    return <>{fallback}</>;
  }

  // Default: Show access denied message
  return (
    <Result
      status="403"
      title="403"
      subTitle={
        t('permissions.accessDenied') || 'Sorry, you are not authorized to access this page.'
      }
      extra={
        <Button type="primary" onClick={() => (globalThis.location.href = redirectTo)}>
          {t('common.backToDashboard') || 'Back to Dashboard'}
        </Button>
      }
    />
  );
};

export default PermissionGuard;
